import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { api, ApiError, streamDocument } from '../../lib/api'
import { humanPath, money } from '../../lib/format'
import type { Draft, InvoiceData, Validation } from '../../lib/types'
import { useAuth } from '../../app/AuthContext'
import { useToast } from '../../app/Toasts'
import FieldInput from './components/FieldInput'
import ItemsGrid from './components/ItemsGrid'
import PageViewer from './components/PageViewer'

const SECTIONS: Array<{ key: keyof InvoiceData; title: string; fields: string[] }> = [
  { key: 'invoice_details', title: 'Invoice', fields: [
    'invoice_number', 'invoice_date', 'due_date', 'order_number',
    'place_of_supply', 'payment_terms',
  ] },
  { key: 'seller', title: 'Seller', fields: ['name', 'gstin', 'address', 'phone', 'email'] },
  { key: 'buyer', title: 'Buyer', fields: ['name', 'gstin', 'address', 'state'] },
  { key: 'tax_summary', title: 'Tax summary', fields: [
    'taxable_amount', 'cgst_total', 'sgst_total', 'igst_total', 'total_tax',
  ] },
  { key: 'totals', title: 'Totals', fields: [
    'subtotal', 'discount', 'shipping', 'round_off', 'grand_total',
    'amount_in_words', 'currency',
  ] },
  { key: 'bank_details', title: 'Bank', fields: ['bank_name', 'account_number', 'ifsc', 'upi_id'] },
  { key: 'additional_fields', title: 'References', fields: ['irn', 'ack_number', 'eway_bill'] },
]

const MONO_FIELDS = new Set([
  'gstin', 'irn', 'ack_number', 'eway_bill', 'ifsc', 'account_number',
  'invoice_number', 'hsn_sac',
])

export default function ReviewPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { push } = useToast()
  const { can } = useAuth()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [editedPaths, setEditedPaths] = useState<Set<string>>(new Set())
  const focusRef = useRef<string | null>(null)

  const [refining, setRefining] = useState(false)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const d = await api.getDraft(id)
        if (alive) setDraft(d)
      } catch (e) {
        push('error', 'Could not load this document',
          e instanceof ApiError ? e.message : undefined)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [id, push])

  // Extraction hands over a usable draft before it has finished. The second
  // opinion lands seconds later and can change which fields are flagged, so
  // pick it up rather than leaving the reviewer looking at stale flags.
  useEffect(() => {
    const stop = streamDocument(id, (_event, payload) => {
      if (payload.refining === true) { setRefining(true); return }
      if (payload.refining === false) {
        setRefining(false)
        if (payload.step === 'second opinion applied') {
          api.getDraft(id)
            .then((d) => {
              setDraft((current) => current && {
                ...current,
                confidence: d.confidence,
                validations: d.validations,
                approvable: d.approvable,
              })
              push('info', 'Second opinion applied',
                'Some fields were re-checked against a second model.')
            })
            .catch(() => {})
        }
      }
    })
    return stop
  }, [id, push])

  const issuesByPath = useMemo(() => {
    const map: Record<string, Validation[]> = {}
    for (const v of draft?.validations ?? []) {
      ;(map[v.path] ??= []).push(v)
    }
    return map
  }, [draft?.validations])

  const commit = useCallback(async (path: string, value: string | null) => {
    setSaving(true)
    setEditedPaths((current) => new Set(current).add(path))
    try {
      const patched = await api.patchDraft(id, { [path]: value })
      setDraft((current) => current && {
        ...current,
        data: patched.data,
        confidence: patched.confidence,
        validations: patched.validations,
        approvable: patched.approvable,
      })
      setSavedAt(Date.now())
    } catch (e) {
      push('error', 'That change was not saved',
        e instanceof ApiError ? e.message : 'Check your connection and try again.')
    } finally {
      setSaving(false)
    }
  }, [id, push])

  const errors = draft?.validations.filter((v) => v.severity === 'error') ?? []
  const warnings = draft?.validations.filter((v) => v.severity === 'warn') ?? []

  const approve = async () => {
    if (!draft) return
    let reason: string | undefined
    if (errors.length > 0) {
      const entered = window.prompt(
        `${errors.length} validation ${errors.length === 1 ? 'error' : 'errors'} remain.\n` +
        'Approving anyway is recorded in the audit log. Why is this correct?',
      )
      if (!entered) return
      reason = entered
    }
    setBusy(true)
    try {
      const result = await api.approve(id, reason)
      push('success', 'Approved',
        `${result.invoice_number} · ${money(result.grand_total)}`)
      navigate('/inbox')
    } catch (e) {
      push('error', 'Could not approve',
        e instanceof ApiError ? e.message : undefined)
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    const reason = window.prompt('Why are you rejecting this document?')
    if (!reason) return
    setBusy(true)
    try {
      await api.reject(id, reason)
      push('info', 'Rejected')
      navigate('/inbox')
    } catch (e) {
      push('error', 'Could not reject', e instanceof ApiError ? e.message : undefined)
    } finally {
      setBusy(false)
    }
  }

  const reextract = async () => {
    setBusy(true)
    try {
      await api.reextract(id)
      push('info', 'Re-reading the document', 'This takes about half a minute.')
      navigate('/inbox')
    } catch (e) {
      push('error', 'Could not re-run extraction',
        e instanceof ApiError ? e.message : undefined)
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="review">
        <div className="skeleton" style={{ height: 520 }} />
        <div style={{ display: 'grid', gap: 12 }}>
          <div className="skeleton" style={{ height: 150 }} />
          <div className="skeleton" style={{ height: 210 }} />
        </div>
      </div>
    )
  }

  if (!draft) {
    return (
      <div className="empty">
        <div className="empty-title">Document not found</div>
        <button className="btn" onClick={() => navigate('/inbox')}>
          Back to the inbox
        </button>
      </div>
    )
  }

  const data = draft.data
  const readOnly = draft.status === 'approved' || !can('draft:write')

  return (
    <div className="review">
      <PageViewer src={api.fileUrl(id)} filename={`Document ${id.slice(0, 8)}`} />

      <div className="fields">
        {/* -------- issue summary, the reviewer's worklist -------- */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="card">
            <div className="card-head">
              <span className="card-title">Needs attention</span>
              <div className="grow" />
              {errors.length > 0 && (
                <span className="pill pill-crit">{errors.length} blocking</span>
              )}
              {warnings.length > 0 && (
                <span className="pill pill-warn">{warnings.length} to check</span>
              )}
            </div>
            <div className="issues">
              {[...errors, ...warnings].slice(0, 8).map((v, i) => (
                <div
                  key={`${v.code}-${v.path}-${i}`}
                  className={`issue ${v.severity === 'error' ? 'error' : 'warn'}`}
                  onClick={() => {
                    focusRef.current = v.path
                    document.getElementById(`f-${v.path}`)?.focus()
                    document.querySelector<HTMLInputElement>(
                      `input[aria-label="${v.path}"]`,
                    )?.focus()
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="issue-path">{humanPath(v.path)}</div>
                    <div className="issue-msg">{v.message}</div>
                  </div>
                  {v.suggestion && !readOnly && (
                    <button
                      className="fix-btn"
                      title={`Use "${v.suggestion}"`}
                      style={{ color: v.severity === 'error' ? 'var(--crit)' : 'var(--warn)' }}
                      onClick={(e) => { e.stopPropagation(); commit(v.path, v.suggestion!) }}
                    >
                      Use {v.suggestion.length > 28
                        ? `${v.suggestion.slice(0, 28)}…`
                        : v.suggestion}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* -------- header sections -------- */}
        {SECTIONS.map((section) => {
          const block = (data[section.key] ?? {}) as Record<string, string | null>
          return (
            <div className="card" key={section.key as string}>
              <div className="card-head">
                <span className="card-title">{section.title}</span>
              </div>
              <div className="card-body">
                <div className="field-grid">
                  {section.fields.map((field) => {
                    const path = `${String(section.key)}.${field}`
                    return (
                      <FieldInput
                        key={path}
                        path={path}
                        name={field}
                        value={block[field] ?? null}
                        confidence={draft.confidence[path]}
                        issues={issuesByPath[path] ?? []}
                        edited={editedPaths.has(path)}
                        mono={MONO_FIELDS.has(field)}
                        onCommit={readOnly ? () => {} : commit}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}

        {/* -------- line items -------- */}
        <div className="card">
          <div className="card-head">
            <span className="card-title">Line items</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {data.items?.length ?? 0} rows
            </span>
          </div>
          <ItemsGrid
            items={data.items ?? []}
            issuesByPath={issuesByPath}
            confidence={draft.confidence}
            editedPaths={editedPaths}
            onCommit={readOnly ? () => {} : commit}
          />
        </div>

        {/* -------- actions -------- */}
        <div className="sticky-actions">
          <div className="autosave">
            {saving
              ? <><span className="spinner" /> Saving…</>
              : refining
                ? <><span className="spinner" /> Checking against a second model…</>
                : savedAt
                  ? <>Saved</>
                  : <>All changes save automatically</>}
          </div>
          <div className="grow" />
          <button className="btn" onClick={reextract} disabled={busy || readOnly}>
            Re-read
          </button>
          <button className="btn btn-danger" onClick={reject} disabled={busy || readOnly}>
            Reject
          </button>
          <button
            className="btn btn-primary"
            onClick={approve}
            disabled={busy || readOnly || !can('invoice:approve')}
            title={
              !can('invoice:approve')
                ? 'Your role cannot approve invoices'
                : errors.length > 0
                  ? 'Approving with errors needs a written reason'
                  : undefined
            }
          >
            {busy && <span className="spinner" />}
            {errors.length > 0 ? 'Approve anyway…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  )
}
